import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { getRaces, getDistricts } from '../lib/api';
import { groupRacesByMonth, MONTH_NAMES_PT, RACE_TYPE_LABELS } from '../lib/utils';
import { Search, MapPin, ExternalLink, Calendar, X, Star, ChevronLeft, ChevronRight, ChevronDown, Plus, Share2, Copy, Check, Heart, Map, List } from 'lucide-react';
import { SubmitRaceModal } from './SubmitRaceModal';

// Lazy load map component for better performance
const RaceMap = lazy(() => import('./RaceMap'));

// Municipalities for Colectividades filter
const COLECTIVIDADES_MUNICIPALITIES = ['Oeiras', 'Cascais', 'Sintra', 'Almada', 'Barreiro', 'Seixal', 'Moita'];

// Standalone embeddable widget for WordPress
export const EmbedCalendar = () => {
  const [races, setRaces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);
  const [activeDistrict, setActiveDistrict] = useState(null);
  const [activeMunicipality, setActiveMunicipality] = useState(null);
  const [isInternational, setIsInternational] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({}); // Track which months show all races
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showDonateMenu, setShowDonateMenu] = useState(false);
  const [copiedMbway, setCopiedMbway] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showColectividadesMenu, setShowColectividadesMenu] = useState(false);
  const [monthsAhead, setMonthsAhead] = useState(2); // Start with 2 months for faster loading
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('aminhacorrida_favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Toggle favorite
  const toggleFavorite = (raceId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(raceId)
        ? prev.filter(id => id !== raceId)
        : [...prev, raceId];
      localStorage.setItem('aminhacorrida_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const isFavorite = (raceId) => favorites.includes(raceId);

  // Get current month (1-12)
  const currentMonth = new Date().getMonth() + 1;

  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [serverWaking, setServerWaking] = useState(false);
  const maxRetries = 15; // 15 tentativas = 30 segundos máximo

  const fetchRaces = useCallback(async () => {
    try {
      if (retryCount === 0) {
        setLoading(true);
      }
      setError(null);
      const params = { is_international: isInternational };
      if (activeType) params.race_type = activeType;
      if (activeMonth) {
        params.month = activeMonth;
        params.from_today = false;
        params.months_ahead = null;
      } else {
        params.months_ahead = monthsAhead;
      }
      if (activeDistrict) params.district = activeDistrict;
      if (activeMunicipality) params.municipality = activeMunicipality;
      if (searchQuery) params.search = searchQuery;
      
      const data = await getRaces(params);
      setRaces(data);
      setRetryCount(0);
      setServerWaking(false);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching races:', err);
      setLoading(false);
      
      // Auto retry - servers might be waking up
      if (retryCount < maxRetries) {
        setServerWaking(true);
        setError('A aguardar servidor...');
        const timer = setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        setServerWaking(false);
        setError('Não foi possível carregar as provas. Por favor recarregue a página.');
      }
    }
  }, [isInternational, activeType, activeMonth, activeDistrict, activeMunicipality, searchQuery, monthsAhead, retryCount]);

  // Trigger fetch when retryCount changes (for auto-retry)
  useEffect(() => {
    if (retryCount > 0) {
      fetchRaces();
    }
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more months
  const loadMoreMonths = async () => {
    setLoadingMore(true);
    setMonthsAhead(prev => prev + 3);
    // The useEffect will trigger a new fetch with updated monthsAhead
  };

  const fetchDistricts = useCallback(async () => {
    try {
      const data = await getDistricts();
      setDistricts(data);
    } catch (err) {
      console.error('Error fetching districts:', err);
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  useEffect(() => {
    const timer = setTimeout(() => fetchRaces(), 300);
    return () => clearTimeout(timer);
  }, [fetchRaces]);

  // Reset loadingMore after races are loaded
  useEffect(() => {
    setLoadingMore(false);
  }, [races]);

  // Filter and group races
  const { groupedRaces, filteredRacesCount } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Filter out completed races unless a specific month is selected
    let filteredRaces = races;
    if (!activeMonth) {
      filteredRaces = races.filter(race => {
        const raceDate = new Date(race.date);
        raceDate.setHours(0, 0, 0, 0);
        return raceDate >= today;
      });
    }
    
    // Filter by favorites if enabled
    if (showOnlyFavorites) {
      filteredRaces = filteredRaces.filter(race => favorites.includes(race.id));
    }
    
    const grouped = groupRacesByMonth(filteredRaces);
    
    // Sort each month: featured first, then by date
    Object.keys(grouped).forEach(month => {
      grouped[month].sort((a, b) => {
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        return new Date(a.date) - new Date(b.date);
      });
    });
    
    return { groupedRaces: grouped, filteredRacesCount: filteredRaces.length };
  }, [races, activeMonth, showOnlyFavorites, favorites]);

  const types = [
    { value: null, label: 'Todas' },
    { value: 'road', label: 'Estrada' },
    { value: 'trail', label: 'Trail' },
    { value: 'colectividades', label: 'Colectividades' },
    { value: 'walk', label: 'Caminhada' },
    { value: 'kids', label: 'Kids' },
  ];

  const resetFilters = () => {
    setSearchQuery('');
    setActiveType(null);
    setActiveMonth(null);
    setActiveDistrict(null);
    setActiveMunicipality(null);
    setShowOnlyFavorites(false);
    setExpandedMonths({});
    setShowColectividadesMenu(false);
  };

  const goToPrevMonth = () => {
    if (activeMonth === null) {
      setActiveMonth(currentMonth > 1 ? currentMonth - 1 : 12);
    } else {
      setActiveMonth(activeMonth > 1 ? activeMonth - 1 : 12);
    }
  };

  const goToNextMonth = () => {
    if (activeMonth === null) {
      setActiveMonth(currentMonth < 12 ? currentMonth + 1 : 1);
    } else {
      setActiveMonth(activeMonth < 12 ? activeMonth + 1 : 1);
    }
  };

  const toggleExpandMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  const borderColors = {
    road: '#334155',
    trail: '#16a34a',
    colectividades: '#8b5cf6',
    walk: '#0ea5e9',
    kids: '#f59e0b'
  };

  const RACES_PER_MONTH = 15;

  // Close donate menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDonateMenu && !e.target.closest('[data-donate-menu]')) {
        setShowDonateMenu(false);
      }
      if (showColectividadesMenu && !e.target.closest('[data-colectividades-menu]')) {
        setShowColectividadesMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDonateMenu]);

  // Generate Google Calendar URL
  const generateGoogleCalendarUrl = (race) => {
    const date = new Date(race.date);
    const startDate = date.toISOString().replace(/-|:|\.\d+/g, '').slice(0, 8);
    const endDate = startDate; // Same day event
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: race.name,
      dates: `${startDate}/${endDate}`,
      details: `${race.race_type === 'road' ? 'Corrida de Estrada' : race.race_type === 'trail' ? 'Trail' : race.race_type === 'walk' ? 'Caminhada' : 'Corrida Kids'}\n\nDistâncias: ${race.distances?.join(', ') || 'N/A'}${race.price ? `\nPreço: ${race.price}` : ''}${race.website ? `\n\nMais info: ${race.website}` : ''}`,
      location: `${race.location}${race.district ? `, ${race.district}` : ''}, Portugal`,
    });
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Share functions
  const shareOnWhatsApp = (race) => {
    const text = `🏃 ${race.name}\n📅 ${new Date(race.date).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })}\n📍 ${race.location}${race.district ? `, ${race.district}` : ''}\n${race.website ? `🔗 ${race.website}` : ''}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareOnFacebook = (race) => {
    const url = race.website || 'https://aminhacorrida.pt';
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`${race.name} - ${race.location}`)}`, '_blank');
  };

  const copyRaceLink = async (race, setCopied) => {
    const text = `${race.name} - ${new Date(race.date).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })} - ${race.location}${race.website ? ` | ${race.website}` : ''}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Hide Emergent badge when component mounts
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      #emergent-badge,
      [id="emergent-badge"],
      a[href*="emergent"],
      [data-testid="emergent-badge"],
      .emergent-badge,
      #__emergent-badge,
      [class*="emergent"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
    
    const removeEmergentBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.display = 'none';
        badge.remove();
      }
      document.querySelectorAll('a[href*="emergent"]').forEach(el => {
        el.style.display = 'none';
        el.remove();
      });
    };
    
    removeEmergentBadge();
    const interval = setInterval(removeEmergentBadge, 100);
    setTimeout(() => clearInterval(interval), 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Render a single race card
  const RaceCard = ({ race }) => {
    const [showShare, setShowShare] = useState(false);
    const [copied, setCopied] = useState(false);
    const isRaceFavorite = isFavorite(race.id);
    
    // Close share menu when clicking outside
    useEffect(() => {
      if (!showShare) return;
      const handleClick = (e) => {
        if (!e.target.closest('[data-share-menu]')) {
          setShowShare(false);
        }
      };
      // Use setTimeout to avoid immediate close from the same click
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick);
      }, 10);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }, [showShare]);
    
    const date = new Date(race.date);
    const day = date.getDate();
    const weekday = date.toLocaleDateString('pt-PT', { weekday: 'short' }).toUpperCase();
    const isFeatured = race.is_featured;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const raceDate = new Date(race.date);
    raceDate.setHours(0, 0, 0, 0);
    const isCompleted = raceDate < today;
    
    return (
      <div
        style={{
          background: isCompleted ? '#f1f5f9' : (isFeatured ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'white'),
          borderRadius: '12px',
          border: isFeatured ? '2px solid #f59e0b' : '1px solid #e2e8f0',
          borderLeft: `5px solid ${isCompleted ? '#94a3b8' : (isFeatured ? '#f59e0b' : (borderColors[race.race_type] || borderColors.road))}`,
          display: 'flex',
          boxShadow: isFeatured && !isCompleted ? '0 4px 12px rgba(245, 158, 11, 0.3)' : 'none',
          position: 'relative',
          opacity: isCompleted ? 0.75 : 1
        }}
      >
        {/* Favorite Heart Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(race.id);
          }}
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: isRaceFavorite ? '#fee2e2' : 'rgba(255,255,255,0.9)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s',
            zIndex: 10
          }}
          title={isRaceFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Heart 
            size={18} 
            color={isRaceFavorite ? '#ef4444' : '#94a3b8'}
            fill={isRaceFavorite ? '#ef4444' : 'none'}
          />
        </button>
        
        {/* Completed Badge */}
        {isCompleted && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: '#64748b',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            textTransform: 'uppercase'
          }}>
            ✓ Concluído
          </div>
        )}
        
        {/* Featured Star Badge */}
        {isFeatured && !isCompleted && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: '#f59e0b',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            textTransform: 'uppercase'
          }}>
            <Star size={12} fill="white" />
            Destaque
          </div>
        )}
        
        {/* Date */}
        <div style={{
          padding: '12px 14px',
          background: isFeatured ? 'rgba(255,255,255,0.6)' : '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '60px'
        }}>
          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase' }}>
            {weekday}
          </span>
          <span style={{ fontSize: '26px', fontWeight: 'bold', color: '#0f172a', fontFamily: "'Barlow Condensed', sans-serif" }}>
            {day}
          </span>
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, padding: '12px', minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', paddingRight: (isFeatured || isCompleted) ? '70px' : '0' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h4 style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: 'bold',
                color: '#0f172a',
                lineHeight: '1.3'
              }}>
                {race.name}
              </h4>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={12} />
                {race.location}{race.district && race.district !== race.location ? `, ${race.district}` : ''}
              </p>
            </div>
            {!isFeatured && !isCompleted && (
              <span style={{
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                background: borderColors[race.race_type] || borderColors.road,
                color: 'white',
                whiteSpace: 'nowrap'
              }}>
                {RACE_TYPE_LABELS[race.race_type] || 'Estrada'}
              </span>
            )}
          </div>
          
          {/* Distances & Link */}
          <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
              {isFeatured && (
                <span style={{
                  padding: '3px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  background: borderColors[race.race_type] || borderColors.road,
                  color: 'white',
                  marginRight: '2px'
                }}>
                  {RACE_TYPE_LABELS[race.race_type] || 'Estrada'}
                </span>
              )}
              {race.distances?.slice(0, 3).map((dist) => (
                <span key={dist} style={{
                  padding: '3px 8px',
                  background: isFeatured ? 'rgba(255,255,255,0.8)' : '#f1f5f9',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#475569',
                  fontWeight: '500'
                }}>
                  {dist}
                </span>
              ))}
              {race.distances?.length > 3 && (
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                  +{race.distances.length - 3}
                </span>
              )}
            </div>
          </div>
          
          {/* Action buttons row */}
          <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {race.website && (
              <a
                href={race.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: isFeatured ? '#b45309' : '#0ea5e9',
                  textDecoration: 'none',
                  fontWeight: '700',
                  padding: '5px 10px',
                  background: isFeatured ? 'rgba(255,255,255,0.8)' : '#f0f9ff',
                  borderRadius: '6px'
                }}
              >
                <ExternalLink size={12} />
                Ver
              </a>
            )}
            
            {/* Google Calendar Button */}
            {!isCompleted && (
              <a
                href={generateGoogleCalendarUrl(race)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#4285f4',
                  textDecoration: 'none',
                  fontWeight: '600',
                  padding: '5px 10px',
                  background: '#e8f0fe',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer'
                }}
                title="Adicionar ao Google Calendar"
              >
                <Calendar size={12} />
                Agendar
              </a>
            )}
            
            {/* Share Button */}
            <div style={{ position: 'relative' }} data-share-menu>
              <button
                onClick={(e) => { e.stopPropagation(); setShowShare(!showShare); }}
                data-testid={`share-btn-${race.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#64748b',
                  fontWeight: '600',
                  padding: '5px 10px',
                  background: '#f1f5f9',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <Share2 size={12} />
                Partilhar
              </button>
              
              {/* Share dropdown - Mobile optimized */}
              {showShare && (
                <div 
                  data-testid={`share-dropdown-${race.id}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'fixed',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
                    padding: '16px',
                    zIndex: 9999,
                    width: 'calc(100% - 40px)',
                    maxWidth: '320px'
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '15px', 
                      color: '#334155',
                      fontWeight: '700'
                    }}>
                      Partilhar prova
                    </p>
                    <button 
                      onClick={() => setShowShare(false)}
                      style={{
                        background: '#f1f5f9',
                        border: 'none',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={14} color="#64748b" />
                    </button>
                  </div>
                  
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>
                    {race.name}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => { shareOnWhatsApp(race); setShowShare(false); }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '12px',
                        background: '#f1f5f9',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        color: '#25D366',
                        fontWeight: '600'
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => { shareOnFacebook(race); setShowShare(false); }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '12px',
                        background: '#f1f5f9',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        color: '#1877f2',
                        fontWeight: '600'
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="#1877f2">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </button>
                    <button
                      onClick={() => { copyRaceLink(race, setCopied); }}
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '12px',
                        background: copied ? '#dcfce7' : '#f1f5f9',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        color: copied ? '#22c55e' : '#64748b',
                        fontWeight: '600'
                      }}
                    >
                      {copied ? <Check size={24} color="#22c55e" /> : <Copy size={24} />}
                      {copied ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              )}
              {/* Backdrop for mobile share menu */}
              {showShare && (
                <div 
                  onClick={() => setShowShare(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.3)',
                    zIndex: 9998
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="amc-embed" style={{ fontFamily: "'Inter', sans-serif", background: '#f8fafc', minHeight: '500px', position: 'relative' }}>
      <style>{`
        [data-testid="emergent-badge"],
        .emergent-badge,
        [class*="emergent"],
        body > div[style*="position: fixed"],
        div[style*="Made with Emergent"] {
          display: none !important;
          visibility: hidden !important;
        }
      `}</style>
      
      {/* Submit Race Modal */}
      <SubmitRaceModal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} />
      
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', padding: '20px 24px', color: 'white' }}>
        {/* Title row with buttons on desktop */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* Title */}
          <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 'bold', margin: 0, textTransform: 'uppercase' }}>
            Calendário de Provas 2026
          </h2>
          
          {/* Action buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* View Toggle - List/Map */}
            <div style={{
              display: 'flex',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px',
              padding: '4px',
              border: '2px solid white'
            }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'list' ? 'white' : 'transparent',
                  color: viewMode === 'list' ? '#0284c7' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                data-testid="view-list-btn"
              >
                <List size={16} />
                Lista
              </button>
              <button
                onClick={() => setViewMode('map')}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'map' ? 'white' : 'transparent',
                  color: viewMode === 'map' ? '#0284c7' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                data-testid="view-map-btn"
              >
                <Map size={16} />
                Mapa
              </button>
            </div>
            
            {/* Submit Race Button */}
            <button
              onClick={() => setShowSubmitModal(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                borderRadius: '10px',
                padding: '8px 14px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              <Plus size={16} />
              Submeter
            </button>
          </div>
        </div>
        
        {/* Subtitle - Always show Portugal */}
        <p style={{ margin: '12px 0', opacity: 0.9, fontSize: '15px' }}>
          Provas em Portugal
        </p>
        
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} size={20} />
          <input
            type="text"
            placeholder="Pesquisar provas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '14px 14px 14px 48px',
              borderRadius: '10px',
              border: 'none',
              fontSize: '16px',
              boxSizing: 'border-box',
              color: '#1e293b',
              backgroundColor: 'white'
            }}
          />
        </div>
      </div>

      {/* Filters Bar - Wrap on mobile */}
      <div style={{ 
        padding: '12px 16px', 
        background: 'white', 
        borderBottom: '1px solid #e2e8f0'
      }}>
        {/* First row: Portugal/Internacional + Type filters */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          alignItems: 'center',
          flexWrap: 'wrap',
          paddingBottom: '8px'
        }}>
          {/* Portugal / Internacional buttons - HIDDEN for public */}
          {/* Os botões foram removidos. Para ver provas internacionais, use o admin */}

          {/* Separator removed as Internacional is hidden */}

          {/* Type Pills */}
          {types.map((type) => (
            type.value === 'colectividades' ? (
              <div key="colectividades" style={{ position: 'relative' }} data-colectividades-menu>
                <button
                  onClick={() => {
                    if (activeType === 'colectividades') {
                      setShowColectividadesMenu(!showColectividadesMenu);
                    } else {
                      setActiveType('colectividades');
                      setActiveMunicipality(null);
                      setShowColectividadesMenu(true);
                    }
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: '1px solid ' + (activeType === 'colectividades' ? '#8b5cf6' : '#e2e8f0'),
                    background: activeType === 'colectividades' ? '#8b5cf6' : 'white',
                    color: activeType === 'colectividades' ? 'white' : '#475569',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Colectividades
                  <ChevronDown size={14} />
                </button>
                
                {/* Municipalities dropdown */}
                {showColectividadesMenu && activeType === 'colectividades' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    marginTop: '8px',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    padding: '8px',
                    zIndex: 1001,
                    minWidth: '160px'
                  }}>
                    <button
                      onClick={() => { setActiveMunicipality(null); setShowColectividadesMenu(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 12px',
                        background: activeMunicipality === null ? '#f1f5f9' : 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: activeMunicipality === null ? '600' : '400',
                        color: '#334155',
                        textAlign: 'left'
                      }}
                    >
                      Todas
                    </button>
                    {COLECTIVIDADES_MUNICIPALITIES.map(mun => (
                      <button
                        key={mun}
                        onClick={() => { setActiveMunicipality(mun); setShowColectividadesMenu(false); }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px 12px',
                          background: activeMunicipality === mun ? '#f1f5f9' : 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: activeMunicipality === mun ? '600' : '400',
                          color: '#334155',
                          textAlign: 'left'
                        }}
                      >
                        {mun}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={type.value || 'all'}
                onClick={() => { setActiveType(type.value); setActiveMunicipality(null); setShowColectividadesMenu(false); }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '20px',
                  border: '1px solid ' + (activeType === type.value ? '#0ea5e9' : '#e2e8f0'),
                  background: activeType === type.value ? '#0ea5e9' : 'white',
                  color: activeType === type.value ? 'white' : '#475569',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {type.label}
              </button>
            )
          ))}

          {/* Donate Button */}
          <div style={{ position: 'relative' }} data-donate-menu>
            <button
              onClick={() => setShowDonateMenu(!showDonateMenu)}
              style={{
                padding: '8px 14px',
                borderRadius: '20px',
                border: '1px solid #f59e0b',
                background: showDonateMenu ? '#fef3c7' : '#fffbeb',
                color: '#b45309',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap'
              }}
            >
              <Heart size={14} fill="#f59e0b" color="#f59e0b" />
              Apoiar
            </button>
            
            {/* Donate Dropdown - Tooltip flutuante */}
            {showDonateMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '-20px',
                marginTop: '8px',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                padding: '16px',
                zIndex: 1001,
                width: '240px'
              }}>
                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '14px', 
                  color: '#334155',
                  fontWeight: '600'
                }}>
                  Ajuda-nos a manter o calendário! ☕
                </p>
                
                {/* PayPal */}
                <a
                  href="https://paypal.me/aminhacorrida1"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    background: '#f1f5f9',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    marginBottom: '8px'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#003087">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.63h6.323c2.615 0 4.506.678 5.62 2.013.46.55.784 1.17.963 1.84.186.696.228 1.5.125 2.39-.36 3.077-2.381 4.642-6.014 4.642H10.06a.77.77 0 0 0-.758.631l-.925 5.731zm5.416-12.24c-.104.88-.626 1.394-1.61 1.394h-.756l.526-3.304h.864c.736 0 1.125.392 1.08 1.12 0 .07-.008.15-.024.24-.016.09-.04.19-.08.31v.24z"/>
                  </svg>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', color: '#003087', fontSize: '14px' }}>PayPal</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>@aminhacorrida1</p>
                  </div>
                </a>
                
                {/* MBWay */}
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText('966084211');
                      setCopiedMbway(true);
                      setTimeout(() => setCopiedMbway(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    background: '#f1f5f9',
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: '#e11d48',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}>MB</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '600', color: '#e11d48', fontSize: '14px' }}>MBWay</p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>966 084 211</p>
                  </div>
                  {copiedMbway ? (
                    <Check size={18} color="#22c55e" />
                  ) : (
                    <Copy size={18} color="#94a3b8" />
                  )}
                </button>
                
                <p style={{ 
                  margin: '10px 0 0 0', 
                  fontSize: '11px', 
                  color: '#94a3b8',
                  textAlign: 'center'
                }}>
                  Obrigado pelo teu apoio! 🙏
                </p>
              </div>
            )}
            {showDonateMenu && (
              <div 
                onClick={() => setShowDonateMenu(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'transparent',
                  zIndex: 1000
                }}
              />
            )}
          </div>
        </div>
        
        {/* Second row: District + Month + Favorites + Clear */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>

        {/* District Filter */}
        {!isInternational && (
          <select
            value={activeDistrict || ''}
            onChange={(e) => setActiveDistrict(e.target.value || null)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: activeDistrict ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
              fontSize: '13px',
              cursor: 'pointer',
              background: activeDistrict ? '#e0f2fe' : 'white',
              color: activeDistrict ? '#0284c7' : '#475569',
              fontWeight: activeDistrict ? '600' : '400'
            }}
          >
            <option value="">Distrito</option>
            {districts.map((district) => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        )}

        {/* Favorites Filter */}
        {favorites.length > 0 && (
          <button
            onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
            style={{
              padding: '8px 12px',
              borderRadius: '20px',
              border: showOnlyFavorites ? '2px solid #ef4444' : '1px solid #e2e8f0',
              background: showOnlyFavorites ? '#fef2f2' : 'white',
              color: showOnlyFavorites ? '#ef4444' : '#475569',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            <Heart size={14} fill={showOnlyFavorites ? '#ef4444' : 'none'} color={showOnlyFavorites ? '#ef4444' : '#94a3b8'} />
            ({favorites.length})
          </button>
        )}

        {/* Month Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
          <button
            onClick={goToPrevMonth}
            style={{
              padding: '6px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Mês anterior"
          >
            <ChevronLeft size={18} color="#475569" />
          </button>
          
          <select
            value={activeMonth || ''}
            onChange={(e) => setActiveMonth(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              padding: '8px 10px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            <option value="">Mês</option>
            {MONTH_NAMES_PT.map((name, i) => (
              <option key={name} value={i + 1}>{name.slice(0, 3)}</option>
            ))}
          </select>
          
          <button
            onClick={goToNextMonth}
            style={{
              padding: '6px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Mês seguinte"
          >
            <ChevronRight size={18} color="#475569" />
          </button>
        </div>

        {(searchQuery || activeType || activeMonth || activeDistrict || showOnlyFavorites) && (
          <button
            onClick={resetFilters}
            style={{
              padding: '6px',
              borderRadius: '50%',
              border: 'none',
              background: '#e0f2fe',
              color: '#0ea5e9',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Limpar filtros"
          >
            <X size={14} />
          </button>
        )}
        </div>
      </div>

      {/* Content Area - List or Map View */}
      <div style={{ padding: '16px' }}>
        {viewMode === 'map' ? (
          /* Map View */
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: '50px', color: '#64748b', fontSize: '16px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '3px solid #e2e8f0',
                borderTopColor: '#0ea5e9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              A carregar mapa...
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          }>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#64748b', fontSize: '16px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#0ea5e9',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                A carregar provas...
              </div>
            ) : (
              <RaceMap races={races.filter(race => {
                // Apply same filters as list view
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                let include = true;
                
                // Filter past races unless specific month selected
                if (!activeMonth) {
                  const raceDate = new Date(race.date);
                  raceDate.setHours(0, 0, 0, 0);
                  include = raceDate >= today;
                }
                
                // Filter by favorites if enabled
                if (showOnlyFavorites && !favorites.includes(race.id)) {
                  include = false;
                }
                
                return include;
              })} />
            )}
          </Suspense>
        ) : (
          /* List View */
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#64748b', fontSize: '16px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#0ea5e9',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                A carregar provas...
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '50px', color: serverWaking ? '#0ea5e9' : '#64748b' }}>
                {serverWaking ? (
                  <>
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      border: '4px solid #e2e8f0',
                      borderTopColor: '#0ea5e9',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 20px'
                    }} />
                    <p style={{ margin: 0, fontSize: '18px', color: '#0ea5e9', fontWeight: '600' }}>
                      A iniciar servidor...
                    </p>
                    <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                      Por favor aguarde alguns segundos
                    </p>
                    {/* Progress bar */}
                    <div style={{ 
                      width: '200px', 
                      height: '6px', 
                      background: '#e2e8f0', 
                      borderRadius: '3px',
                      margin: '20px auto 0',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${Math.min((retryCount / maxRetries) * 100, 100)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #0ea5e9, #0284c7)',
                        borderRadius: '3px',
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </>
                ) : (
                  <>
                    <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '16px', color: '#64748b' }}>{error}</p>
                    <button
                      onClick={() => { setRetryCount(0); setServerWaking(true); fetchRaces(); }}
                      style={{
                        marginTop: '16px',
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Tentar novamente
                    </button>
                  </>
                )}
              </div>
            ) : filteredRacesCount === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>
            <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '16px' }}>Nenhuma prova encontrada</p>
            {!activeMonth && (
              <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
                Selecione um mês para ver provas passadas
              </p>
            )}
          </div>
        ) : (
          Object.entries(groupedRaces)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([month, monthRaces]) => {
              const isExpanded = expandedMonths[month];
              const visibleRaces = isExpanded ? monthRaces : monthRaces.slice(0, RACES_PER_MONTH);
              const hasMore = monthRaces.length > RACES_PER_MONTH;
              const hiddenCount = monthRaces.length - RACES_PER_MONTH;
              
              return (
                <div key={month} style={{ marginBottom: '32px' }}>
                  {/* Month Header */}
                  <h3 style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: '38px',
                    fontWeight: 'bold',
                    color: '#cbd5e1',
                    textTransform: 'uppercase',
                    margin: '0 0 16px 0',
                    letterSpacing: '3px'
                  }}>
                    {MONTH_NAMES_PT[parseInt(month) - 1]}
                  </h3>
                  
                  {/* Race Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {visibleRaces.map((race) => (
                      <RaceCard key={race.id} race={race} />
                    ))}
                  </div>
                  
                  {/* Ver Mais Button */}
                  {hasMore && (
                    <button
                      onClick={() => toggleExpandMonth(month)}
                      style={{
                        width: '100%',
                        marginTop: '16px',
                        padding: '14px 24px',
                        borderRadius: '10px',
                        border: '2px solid #0ea5e9',
                        background: 'white',
                        color: '#0ea5e9',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      <ChevronDown 
                        size={20} 
                        style={{ 
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }} 
                      />
                      {isExpanded 
                        ? 'Ver menos' 
                        : `Ver mais ${hiddenCount} ${hiddenCount === 1 ? 'prova' : 'provas'}`
                      }
                    </button>
                  )}
                </div>
              );
            })
        )}
        
        {/* Load More Button - only show in list view, not filtering by specific month and monthsAhead < 12 */}
        {viewMode === 'list' && !loading && !activeMonth && monthsAhead < 12 && filteredRacesCount > 0 && (
          <button
            onClick={loadMoreMonths}
            disabled={loadingMore}
            style={{
              width: '100%',
              marginTop: '24px',
              padding: '16px 24px',
              borderRadius: '10px',
              border: '2px solid #0ea5e9',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              color: 'white',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: loadingMore ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: loadingMore ? 0.7 : 1
            }}
          >
            {loadingMore ? (
              <>Carregando...</>
            ) : (
              <>
                <ChevronDown size={20} />
                Carregar mais provas
              </>
            )}
          </button>
        )}
        
        {/* Results count */}
        {!loading && filteredRacesCount > 0 && viewMode === 'list' && (
          <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', marginTop: '28px' }}>
            {filteredRacesCount} {filteredRacesCount === 1 ? 'prova encontrada' : 'provas encontradas'}
            {!activeMonth && monthsAhead < 12 && ` (próximos ${monthsAhead} meses)`}
            {!activeMonth && monthsAhead >= 12 && ' (ano completo)'}
          </p>
        )}
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '20px', textAlign: 'center', borderTop: '1px solid #e2e8f0', background: 'white' }}>
        <a 
          href="https://aminhacorrida.pt" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ fontSize: '14px', color: '#0ea5e9', textDecoration: 'none', fontWeight: '600' }}
        >
          A Minha Corrida © 2026
        </a>
      </div>
    </div>
  );
};

export default EmbedCalendar;
